import { protectedApi } from '@api/index';
import { formatApiResponse } from '@utils/helpers';
import { APIdateFormat } from '@utils/dates';

export async function DashboardApi(filter) {
  try {
    let dateFilter =
      filter.startDate && filter.endDate
        ? `?start_date=${APIdateFormat(filter.startDate)}&end_date=${APIdateFormat(
            filter.endDate
          )}&clinic_id=${filter.clinic_id}`
        : '';
    const res = await protectedApi.get(`/calls/dashboard${dateFilter}`);
    return formatApiResponse(res);
  } catch (e) {
    return formatApiResponse(e);
  }
}

export async function NewStatusDashboardApi({ clinic_id, category, week_window, month_mode, page, limit } = {}) {
  try {
    const params = new URLSearchParams();
    if (clinic_id !== undefined && clinic_id !== null) params.set('clinic_id', String(clinic_id));
    if (category && category !== 'all') params.set('category', String(category));
    if (week_window) params.set('week_window', String(week_window));
    if (month_mode) params.set('month_mode', String(month_mode));
    if (page) params.set('page', String(page));
    if (limit) params.set('limit', String(limit));
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await protectedApi.get(`/calls/new-status-dashboard${query}`);
    return formatApiResponse(res);
  } catch (e) {
    return formatApiResponse(e);
  }
}

export async function CategoryOverviewDashboardApi() {
  try {
    const res = await protectedApi.get('/calls/category-overview-dashboard');
    return formatApiResponse(res);
  } catch (e) {
    return formatApiResponse(e);
  }
}

export async function TriageStatusDashboardApi({ view, start_date, end_date } = {}) {
  try {
    const params = new URLSearchParams();
    if (view) params.set('view', String(view));
    if (view === 'custom' && start_date && end_date) {
      params.set('start_date', String(start_date));
      params.set('end_date', String(end_date));
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await protectedApi.get(`/calls/triage-status-dashboard${query}`);
    return formatApiResponse(res);
  } catch (e) {
    return formatApiResponse(e);
  }
}

export async function LeadsPerformanceDashboardApi({
  clinic_id,
  view,
  category,
  start_date,
  end_date,
  prev_start_date,
  prev_end_date,
  page,
  limit
} = {}) {
  try {
    const params = new URLSearchParams();
    if (clinic_id !== undefined && clinic_id !== null) params.set('clinic_id', String(clinic_id));
    if (view) params.set('view', String(view));
    if (category && category !== 'all') params.set('category', String(category));
    const p = page != null && page >= 1 ? Math.floor(Number(page)) : 1;
    const l = limit != null && limit >= 1 ? Math.floor(Number(limit)) : 20;
    params.set('page', String(p));
    params.set('limit', String(l));
    if (view === 'custom' && start_date && end_date) {
      params.set('start_date', String(start_date));
      params.set('end_date', String(end_date));
      if (prev_start_date && prev_end_date) {
        params.set('prev_start_date', String(prev_start_date));
        params.set('prev_end_date', String(prev_end_date));
      }
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await protectedApi.get(`/calls/performance-dashboard${query}`);
    return formatApiResponse(res);
  } catch (e) {
    return formatApiResponse(e);
  }
}

export async function exportLeadsPerformanceDashboardToExcel({
  clinic_id,
  view,
  category,
  start_date,
  end_date,
  prev_start_date,
  prev_end_date
} = {}) {
  const payload = {
    export_type: 'excel',
    clinic_id: clinic_id ?? 0,
    view: view || 'weekly',
    category: category || 'all'
  };
  if (view === 'custom' && start_date && end_date) {
    payload.start_date = start_date;
    payload.end_date = end_date;
    if (prev_start_date && prev_end_date) {
      payload.prev_start_date = prev_start_date;
      payload.prev_end_date = prev_end_date;
    }
  }
  const res = await protectedApi.post(`/calls/performance-dashboard`, payload, {
    responseType: 'blob'
  });
  return res;
}

// The same analysis for several comparison windows in one answer:
// { clinic, country, windows: { weekly: {...}, last_2_weeks: {...}, monthly: {...} } }.
export async function LeadDropAnalysisMatrixApi({ clinic_id, country, windows } = {}) {
  try {
    const params = new URLSearchParams();
    if (clinic_id) params.set('clinic_id', String(clinic_id));
    if (country && country !== 'all') params.set('country', String(country));
    if (windows && windows.length) params.set('windows', windows.join(','));
    params.set('extra', '1');
    const res = await protectedApi.get(`/calls/lead-drop-analysis-matrix?${params.toString()}`, {
      timeout: 180000
    });
    return formatApiResponse(res);
  } catch (e) {
    return formatApiResponse(e);
  }
}

export async function LeadDropAnalysisApi({
  clinic_id,
  view,
  country,
  start_date,
  end_date,
  prev_start_date,
  prev_end_date,
  extra
} = {}) {
  try {
    const params = new URLSearchParams();
    if (clinic_id) params.set('clinic_id', String(clinic_id));
    if (view) params.set('view', String(view));
    if (country && country !== 'all') params.set('country', String(country));
    if (extra) params.set('extra', '1');
    if (view === 'custom' && start_date && end_date) {
      params.set('start_date', String(start_date));
      params.set('end_date', String(end_date));
      if (prev_start_date && prev_end_date) {
        params.set('prev_start_date', String(prev_start_date));
        params.set('prev_end_date', String(prev_end_date));
      }
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await protectedApi.get(`/calls/lead-drop-analysis${query}`, {
      timeout: 120000
    });
    return formatApiResponse(res);
  } catch (e) {
    return formatApiResponse(e);
  }
}

export async function exportLeadDropAnalysisToExcel({ clinic_id, country } = {}) {
  const res = await protectedApi.post(
    `/calls/lead-drop-analysis`,
    {
      export_type: 'excel',
      clinic_id: clinic_id ?? 0,
      country: country || 'all'
    },
    { responseType: 'blob', timeout: 180000 }
  );
  return res;
}

export async function exportSeoClinicListToExcel({ view, country } = {}) {
  const res = await protectedApi.post(
    `/calls/seo-clinic-list`,
    {
      export_type: 'excel',
      view: view || 'weekly',
      country: country || 'all'
    },
    { responseType: 'blob', timeout: 180000 }
  );
  return res;
}

export async function SeoClinicListApi({ view } = {}) {
  try {
    const params = new URLSearchParams();
    if (view) params.set('view', String(view));
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await protectedApi.get(`/calls/seo-clinic-list${query}`, { timeout: 60000 });
    return formatApiResponse(res);
  } catch (e) {
    return formatApiResponse(e);
  }
}

export async function SeoClinicClicksAllApi({ view, country } = {}) {
  try {
    const params = new URLSearchParams();
    if (view) params.set('view', String(view));
    if (country && country !== 'all') params.set('country', String(country));
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await protectedApi.get(`/calls/seo-clinic-clicks-all${query}`, { timeout: 120000 });
    return formatApiResponse(res);
  } catch (e) {
    return formatApiResponse(e);
  }
}

export async function SeoClinicClicksApi({ clinic_id, view, country } = {}) {
  try {
    const params = new URLSearchParams();
    if (clinic_id) params.set('clinic_id', String(clinic_id));
    if (view) params.set('view', String(view));
    if (country && country !== 'all') params.set('country', String(country));
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await protectedApi.get(`/calls/seo-clinic-clicks${query}`, { timeout: 60000 });
    return formatApiResponse(res);
  } catch (e) {
    return formatApiResponse(e);
  }
}

export async function LeadDropAIAnalysisApi(payload) {
  try {
    const res = await protectedApi.post(`/calls/lead-drop-ai-analysis`, payload || {}, {
      timeout: 120000
    });
    return formatApiResponse(res);
  } catch (e) {
    return formatApiResponse(e);
  }
}

export async function MissedCallAnalyticsApi({ clinic_id, view, start_date, end_date } = {}) {
  try {
    const params = new URLSearchParams();
    if (clinic_id !== undefined && clinic_id !== null) params.set('clinic_id', String(clinic_id));
    if (view) params.set('view', String(view));
    if (view === 'custom' && start_date && end_date) {
      params.set('start_date', String(start_date));
      params.set('end_date', String(end_date));
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await protectedApi.get(`/calls/missed-call-analytics${query}`);
    return formatApiResponse(res);
  } catch (e) {
    return formatApiResponse(e);
  }
}
